import DateInput from '../../components/DateInput';
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Star, CheckCircle2, XCircle, Trash2, MessageSquareQuote, Filter, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { safeConfirm } from '../../lib/confirm';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending', accent: 'text-[#8A4A00] bg-[#FFE0B3]' },
  { key: 'approved', label: 'Approved', accent: 'text-[#3A664D] bg-[#D4F0DC]' },
  { key: 'rejected', label: 'Rejected', accent: 'text-[#B83A3A] bg-[#FBD9D2]' },
  { key: 'all', label: 'All', accent: 'text-fg-muted bg-surface-2' },
];

export default function AdminReviews() {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [showBackfill, setShowBackfill] = useState(false);

  const load = () => {
    setLoading(true);
    const url = tab === 'all' ? '/reviews' : `/reviews?status=${tab}`;
    Promise.all([
      api.get(url).then(r => r.data),
      api.get('/reviews/stats').then(r => r.data).catch(() => stats),
    ]).then(([rows, s]) => {
      setItems(rows);
      setStats(s);
      setLoading(false);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const moderate = async (review_id, status, note) => {
    setBusy(review_id);
    try {
      await api.post(`/reviews/${review_id}/moderate`, { status, moderator_note: note || null });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusy(''); }
  };

  const remove = async (review_id) => {
    if (!await safeConfirm('Permanently delete this review? This cannot be undone.')) return;
    setBusy(review_id);
    try {
      await api.delete(`/reviews/${review_id}`);
      load();
    } finally { setBusy(''); }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="mb-6 flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">REPUTATION</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Patient reviews</h1>
          <p className="text-fg-muted mt-2 text-sm">
            Moderate every review before it goes live. Approved reviews appear on the public landing page and clinician profiles.
          </p>
        </div>
        <button onClick={()=>setShowBackfill(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="reviews-backfill-open">
          <Plus className="w-4 h-4"/> Backfill historical review
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="reviews-tabs">
        {STATUS_TABS.map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)}
                  data-testid={`reviews-tab-${t.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${tab===t.key ? 'border-brand text-brand bg-[#F2EBDB]' : 'border-soft text-fg-muted hover:text-fg'}`}>
            <Filter className="w-3 h-3 inline -mt-0.5 mr-1"/>
            {t.label}
            {t.key !== 'all' && (
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${t.accent}`}>
                {stats[t.key] || 0}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-fg-muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bento-card text-center py-12">
          <MessageSquareQuote className="w-10 h-10 text-fg-muted mx-auto mb-3"/>
          <div className="font-heading font-semibold">No {tab !== 'all' ? tab : ''} reviews</div>
          <div className="text-xs text-fg-muted mt-1">Reviews from completed appointments will land here for moderation.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(r => (
            <ReviewCard key={r.review_id} review={r} busy={busy===r.review_id}
                        onApprove={(note)=>moderate(r.review_id, 'approved', note)}
                        onReject={(note)=>moderate(r.review_id, 'rejected', note)}
                        onDelete={()=>remove(r.review_id)}/>
          ))}
        </div>
      )}

      {showBackfill && (
        <BackfillModal onClose={()=>setShowBackfill(false)} onSaved={()=>{ setShowBackfill(false); load(); }}/>
      )}
    </div>
  );
}

function ReviewCard({ review, busy, onApprove, onReject, onDelete }) {
  const [note, setNote] = useState(review.moderator_note || '');
  const [showNote, setShowNote] = useState(false);
  return (
    <div className="bento-card" data-testid={`review-${review.review_id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <StarRow rating={review.rating}/>
            <StatusBadge status={review.status}/>
          </div>
          <p className="text-sm text-fg-secondary leading-relaxed whitespace-pre-wrap">{review.text}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fg-muted">
            <span><span className="text-fg-muted">By</span> <span className="text-fg font-medium">{review.author_name}</span></span>
            {review.author_email && <span className="font-mono">{review.author_email}</span>}
            {review.clinician_name && <span><span className="text-fg-muted">For</span> <span className="text-fg">{review.clinician_name}</span></span>}
            <span>{new Date(review.created_at).toLocaleString()}</span>
          </div>
          {review.moderator_note && (
            <div className="mt-2 text-xs surface border border-soft rounded p-2 text-fg-secondary">
              <span className="text-fg-muted uppercase tracking-wider mr-2">Note</span>{review.moderator_note}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 min-w-[160px]">
          {review.status === 'pending' ? (
            <>
              <button onClick={()=>setShowNote(!showNote)} className="text-xs text-fg-muted underline self-start">
                {showNote ? 'Hide note' : 'Add private note (optional)'}
              </button>
              {showNote && (
                <textarea value={note} onChange={e=>setNote(e.target.value)}
                          placeholder="Internal note (not visible to patient)"
                          className="input text-xs min-h-[60px]" data-testid={`note-${review.review_id}`}/>
              )}
              <button onClick={()=>onApprove(note)} disabled={busy}
                      className="btn-primary text-xs inline-flex items-center justify-center gap-1.5" data-testid={`approve-${review.review_id}`}>
                <CheckCircle2 className="w-4 h-4"/> Approve
              </button>
              <button onClick={()=>onReject(note)} disabled={busy}
                      className="btn-secondary text-xs inline-flex items-center justify-center gap-1.5" data-testid={`reject-${review.review_id}`}>
                <XCircle className="w-4 h-4"/> Reject
              </button>
            </>
          ) : (
            <>
              {review.status === 'approved' && (
                <button onClick={()=>onReject(review.moderator_note)} disabled={busy}
                        className="btn-secondary text-xs inline-flex items-center justify-center gap-1.5" data-testid={`unpublish-${review.review_id}`}>
                  <XCircle className="w-4 h-4"/> Unpublish
                </button>
              )}
              {review.status === 'rejected' && (
                <button onClick={()=>onApprove(review.moderator_note)} disabled={busy}
                        className="btn-primary text-xs inline-flex items-center justify-center gap-1.5" data-testid={`republish-${review.review_id}`}>
                  <CheckCircle2 className="w-4 h-4"/> Approve
                </button>
              )}
              <button onClick={onDelete} disabled={busy}
                      className="text-xs text-[#B83A3A] inline-flex items-center justify-center gap-1.5" data-testid={`delete-${review.review_id}`}>
                <Trash2 className="w-3.5 h-3.5"/> Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StarRow({ rating }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1,2,3,4,5].map(n => (
        <Star key={n} className={`w-4 h-4 ${n <= rating ? 'fill-[#FFB000] text-[#FFB000]' : 'text-[#E8DFC8]'}`}/>
      ))}
      <span className="ml-1 text-xs font-medium text-fg-muted">{rating}/5</span>
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:  { c: 'bg-[#FFE0B3] text-[#8A4A00]', label: 'Pending' },
    approved: { c: 'bg-[#D4F0DC] text-[#3A664D]', label: 'Published' },
    rejected: { c: 'bg-[#FBD9D2] text-[#B83A3A]', label: 'Rejected' },
  };
  const m = map[status] || map.pending;
  return <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${m.c}`}>{m.label}</span>;
}


// ─── Backfill modal — admin imports a historical review (paper, Google, etc.) ──
function BackfillModal({ onClose, onSaved }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [scope, setScope] = useState('clinic_solo');
  const [source, setSource] = useState('google');
  const [targetId, setTargetId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [clinicians, setClinicians] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);

  // Load target lists once.
  useEffect(() => {
    api.get('/clinicians').then(r => setClinicians(r.data || [])).catch(()=>{});
    api.get('/fitness/trainers').then(r => setTrainers(r.data || [])).catch(()=>{});
    api.get('/clinic/locations').then(r => setLocations(r.data || [])).catch(()=>{});
  }, []);

  const needsTarget = ['therapist', 'trainer', 'fitness_instructor'].includes(scope);
  const targets = scope === 'therapist'
    ? clinicians.map(c => ({ id: c.clinician_id || c.user_id, label: c.full_name || c.name }))
    : (scope === 'trainer' || scope === 'fitness_instructor')
      ? trainers.map(t => ({ id: t.trainer_id, label: t.name }))
      : [];

  const submit = async () => {
    if (!text || text.length < 4) { toast.error('Review text is too short.'); return; }
    if (!authorName) { toast.error('Author name is required.'); return; }
    if (needsTarget && !targetId) { toast.error('Pick the person this review is about.'); return; }
    setSaving(true);
    try {
      await api.post('/reviews/backfill', {
        rating, text, author_name: authorName,
        review_date: date, scope, target_id: targetId || null,
        location_id: locationId || null,
        source,
      });
      toast.success('Review imported.');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="reviews-backfill-modal">
      <div className="bg-bg-primary rounded-lg max-w-xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-bold">Backfill historical review</h3>
          <button onClick={onClose}><X className="w-4 h-4"/></button>
        </div>
        <p className="text-xs text-fg-muted mb-4">
          Use this to import reviews patients left elsewhere (Google, Facebook, paper) before they used Physiogenic.
          These go straight to <strong>Approved</strong> and show on the public profile.
        </p>

        <FormField label="Star rating">
          <div className="flex items-center gap-1" data-testid="bf-rating">
            {[1,2,3,4,5].map(n => (
              <button key={n} type="button" onClick={()=>setRating(n)}
                      className="p-0.5" data-testid={`bf-star-${n}`}>
                <Star className={`w-7 h-7 ${n <= rating ? 'fill-[#FFB000] text-[#FFB000]' : 'text-[#E8DFC8]'}`}/>
              </button>
            ))}
            <span className="ml-2 text-sm font-medium text-fg-muted">{rating}/5</span>
          </div>
        </FormField>

        <FormField label="Author name (as it should appear publicly)">
          <input className="input" value={authorName} onChange={e=>setAuthorName(e.target.value)}
                 placeholder="e.g. Anna K." data-testid="bf-author"/>
        </FormField>

        <FormField label="Review date">
          <DateInput value={date} onChange={e=>setDate(e.target.value)} data-testid="bf-date"/>
        </FormField>

        <FormField label="Review note">
          <textarea className="input min-h-[100px]" value={text} onChange={e=>setText(e.target.value)}
                    placeholder="What the patient wrote…" data-testid="bf-text"/>
        </FormField>

        <FormField label="Attach this review to…">
          <select className="input" value={scope} onChange={e=>{setScope(e.target.value); setTargetId('');}} data-testid="bf-scope">
            <option value="clinic_solo">Solo clinic — overall reputation</option>
            <option value="clinic_multi">Multi-clinic — overall + specific branch</option>
            <option value="all_clinics">All clinics in the network</option>
            <option value="therapist">A specific clinician / therapist</option>
            <option value="trainer">A specific trainer</option>
            <option value="fitness_instructor">A specific fitness instructor</option>
          </select>
        </FormField>

        {scope === 'clinic_multi' && (
          <FormField label="Branch / location">
            <select className="input" value={locationId} onChange={e=>setLocationId(e.target.value)} data-testid="bf-location">
              <option value="">All branches</option>
              {locations.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
            </select>
          </FormField>
        )}

        {needsTarget && (
          <FormField label={scope === 'therapist' ? 'Which clinician?' : 'Which trainer/instructor?'}>
            <select className="input" value={targetId} onChange={e=>setTargetId(e.target.value)} data-testid="bf-target">
              <option value="">Choose…</option>
              {targets.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </FormField>
        )}

        <FormField label="Source (where was this review originally?)">
          <select className="input" value={source} onChange={e=>setSource(e.target.value)} data-testid="bf-source">
            <option value="google">Google</option>
            <option value="facebook">Facebook</option>
            <option value="paper">Paper review</option>
            <option value="word_of_mouth">Word of mouth</option>
            <option value="other">Other</option>
          </select>
        </FormField>

        <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-3">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary text-sm disabled:opacity-50" data-testid="bf-save">
            {saving ? 'Saving…' : 'Import review'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="text-xs uppercase tracking-wider text-fg-muted font-medium mb-1.5">{label}</div>
      {children}
    </label>
  );
}
